import { useCallback, useEffect, useState } from 'react'

import { isBefore, setHours, setMinutes } from 'date-fns'
import TimePicker from 'react-time-picker'

import { Button } from '@/components/ui/button.tsx'
import { Calendar } from '@/components/ui/calendar/calendar.tsx'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { AmPmSwitch } from '@/modules/online-mass-messages/ui/AmPmSwitch/AmPmSwitch'
import {
  getFormattedTime,
  getIsMinTime,
  getIsPM,
} from '@/modules/online-mass-messages/utils'
import { classNames } from '@/utils/helpers'

interface CalendarDateTimePickerProps {
  date: Date | undefined
  onSave: (value: Date | undefined) => void
  className?: string
  popoverClassName?: string
  minDate: Date
  disabled?: boolean
  trigger?: React.ReactNode
  title?: string
  defaultDate?: Date
  open?: boolean
  onOpenChange?: (state: boolean) => void
}

export const CalendarDateTimePicker = ({
  className,
  popoverClassName,
  date,
  onSave,
  minDate,
  trigger,
  title = 'Select a date and time',
  defaultDate,
  open,
  onOpenChange,
  disabled,
}: CalendarDateTimePickerProps) => {
  const [localDate, setLocalDate] = useState(date || defaultDate || new Date())
  const [isPM, setIsPm] = useState(getIsPM(localDate))

  useEffect(() => {
    if (!date) {
      setLocalDate(defaultDate || new Date())
    }
  }, [date, defaultDate])

  const _minTime = getIsMinTime(localDate, minDate)
    ? getFormattedTime(minDate)
    : undefined

  const handleDateChange = (value: Date | undefined) => {
    if (value) {
      setLocalDate((prev) => {
        const newDate = new Date(value)

        newDate.setHours(prev.getHours())
        newDate.setMinutes(prev.getMinutes())

        return newDate
      })
    }
  }

  const handleTimeChange = useCallback(
    (value: string | null) => {
      if (typeof value === 'string') {
        let newDate = new Date(localDate)
        const [hours, minutes] = value.split(':')

        newDate = setMinutes(
          setHours(newDate, parseInt(hours, 10)),
          parseInt(minutes, 10),
        )

        setLocalDate(newDate)
      }
    },
    [localDate],
  )

  const handleAmPmChange = () => {
    if (isPM) {
      const newDate = setHours(localDate, localDate.getHours() - 12)

      if (isBefore(newDate, minDate)) {
        return
      }

      setLocalDate(newDate)
      setIsPm(false)
    } else {
      const newDate = setHours(localDate, localDate.getHours() + 12)

      if (isBefore(newDate, minDate)) {
        return
      }
      setLocalDate(newDate)
      setIsPm(true)
    }
  }

  const handleSave = () => {
    if (localDate !== date) {
      onSave?.(localDate)
    }
  }

  const popoverOpen = disabled ? false : open

  const handlePopoverOpenChange = useCallback(
    (state: boolean) => {
      if (disabled) {
        return
      }

      onOpenChange?.(state)
    },
    [disabled, onOpenChange],
  )

  useEffect(() => {
    if (disabled) {
      onOpenChange?.(false)
    }
  }, [disabled, onOpenChange])

  return (
    <div className={classNames('grid gap-2', className)}>
      <Popover
        open={popoverOpen}
        onOpenChange={handlePopoverOpenChange}
      >
        <PopoverTrigger asChild>
          <span
            className={classNames(
              'inline-flex',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
            aria-disabled={disabled}
          >
            {trigger}
          </span>
        </PopoverTrigger>
        <PopoverContent
          className={classNames(
            'z-100 flex w-auto flex-col p-4',
            popoverClassName,
          )}
          align="start"
        >
          <span className="mb-3 text-base font-semibold">{title}</span>
          <div className="rounded-md border px-6 py-4">
            <Calendar
              initialFocus
              mode="single"
              defaultMonth={date}
              selected={localDate}
              onSelect={handleDateChange}
              numberOfMonths={1}
              disabled={{ before: minDate }}
            />
            <div className="mt-3 flex items-center">
              <span className="mr-auto text-sm">Time</span>
              <TimePicker
                amPmAriaLabel="Select AM/PM"
                className="h-full rounded-md bg-secondary-100/50 text-title placeholder:text-title dark:bg-secondary-600"
                maxDetail="second"
                format="hh:mm a"
                value={localDate}
                clearIcon={null}
                clockIcon={null}
                minTime={_minTime}
                onChange={handleTimeChange}
                disableClock
              />
              <AmPmSwitch
                handleToggle={handleAmPmChange}
                className="ml-2 bg-secondary-100/50 dark:bg-secondary-600"
                onColor="#fff"
                isOn={isPM}
              />
            </div>
          </div>
          <div className="mt-3">
            <PopoverClose className="flex w-full gap-4">
              <Button
                type="button"
                variant="outline"
                className="flex flex-1 items-center gap-2"
              >
                Cancel
              </Button>
              <Button
                className="flex flex-1 items-center gap-2"
                onClick={handleSave}
              >
                Save
              </Button>
            </PopoverClose>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
